/*
 * IntakeReadinessCheck
 * Six intake questions from the article, answered Yes / No / Not sure.
 *
 * Rules:
 * - "Yes"      -> no action shown for that question.
 * - "No"       -> listed under "Fix next" with its specific fix and a link.
 * - "Not sure" -> listed under "Check first" with the same fix and link
 *                 (an unknown is treated as something to verify, not a pass).
 * - Results update as the reader answers. Once all six are answered and all are "Yes",
 *   the reader is pointed to the free audit instead.
 * No scores, benchmarks, tracking, storage or network calls.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
 
type Answer = "yes" | "no" | "unsure";
 
type Question = {
  id: string;
  text: string;
  fix: string;
  link: { href: string; text: string };
};
 
const QUESTIONS: Question[] = [
  {
    id: "phone",
    text: "Is it easy to contact the firm by phone, especially on mobile?",
    fix: "Put a tap-to-call number in the header of every page so nobody has to scroll or hunt for it.",
    link: { href: "/services/law-firm-website-development", text: "Web design for law firms" },
  },
  {
    id: "process",
    text: "Is the consultation process clearly explained?",
    fix: "Add a short “what happens next” section to practice-area and contact pages: who gets back to them, how, and what to have ready.",
    link: { href: "/services/lead-generation-for-law-firms", text: "Lead generation for law firms" },
  },
  {
    id: "form",
    text: "Does the form ask only for the information you need?",
    fix: "Cut the form down to what intake needs for first contact and collect the rest on the call.",
    link: { href: "/services/law-firm-website-development", text: "Web design for law firms" },
  },
  {
    id: "tracking",
    text: "Are calls and form submissions being tracked?",
    fix: "Set up call tracking and GA4 key events for calls, form submissions and chat clicks.",
    link: { href: "/services/law-firm-marketing-analytics", text: "Analytics & reporting" },
  },
  {
    id: "speed",
    text: "Does someone own reviewing new inquiries, with a response time the team keeps?",
    fix: "Name one person responsible for new inquiries and agree a response time the whole team sticks to.",
    link: { href: "/services/lead-generation-for-law-firms", text: "Lead generation for law firms" },
  },
  {
    id: "source",
    text: "Can the firm see which marketing channel brought each inquiry?",
    fix: "Record the source of every inquiry with UTM tags, call-tracking numbers per channel, and a source field in intake.",
    link: { href: "/services/law-firm-marketing-analytics", text: "Analytics & reporting" },
  },
];
 
const OPTIONS: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];
 
export default function IntakeReadinessCheck() {
  const [answers, setAnswers] = useState<Record<string, Answer | undefined>>({});
 
  const answeredCount = QUESTIONS.filter((q) => answers[q.id]).length;
  const fixNext = QUESTIONS.filter((q) => answers[q.id] === "no");
  const checkFirst = QUESTIONS.filter((q) => answers[q.id] === "unsure");
  const allDone = answeredCount === QUESTIONS.length;
  const allYes = allDone && fixNext.length === 0 && checkFirst.length === 0;
 
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
 
  const renderList = (title: string, items: Question[]) =>
    items.length > 0 && (
      <div className="mt-3">
        <p className="text-sm font-semibold">{title}</p>
        <ul className="mt-2 space-y-3">
          {items.map((q) => (
            <li key={q.id} className="rounded-md border border-border bg-card p-3 text-sm">
              <p className="font-medium">{q.text}</p>
              <p className="mt-1 text-muted-foreground">{q.fix}</p>
              <Link to={q.link.href} className={`mt-1 inline-block font-medium underline underline-offset-4 ${focusRing}`}>
                {q.link.text} →
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
 
  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Intake readiness check</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Answer honestly for your own firm. You’ll get a list of what to fix, with nothing saved or sent anywhere.
      </p>
 
      <ol className="mt-5 space-y-4">
        {QUESTIONS.map((q, i) => {
          const labelId = `intake-q-${q.id}`;
          return (
            <li key={q.id} className="rounded-lg border border-border p-4">
              <p id={labelId} className="text-sm font-medium">
                {i + 1}. {q.text}
              </p>
              <div role="group" aria-labelledby={labelId} className="mt-3 flex flex-wrap gap-2">
                {OPTIONS.map((o) => {
                  const pressed = answers[q.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={pressed}
                      onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: o.value }))}
                      className={`rounded-md border px-4 py-1.5 text-sm ${focusRing} ${
                        pressed
                          ? "border-border bg-primary text-primary-foreground"
                          : "border-input bg-card text-card-foreground"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>
 
      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        <p className="text-sm text-muted-foreground">
          {answeredCount} of {QUESTIONS.length} answered
        </p>
        {answeredCount === 0 && (
          <p className="mt-1 text-sm">Your gaps will appear here as you answer.</p>
        )}
        {renderList("Fix next", fixNext)}
        {renderList("Check first (you weren’t sure)", checkFirst)}
        {allYes && (
          <p className="mt-2 text-sm">
            Your intake basics are in place. The next gains are more likely on the pages themselves.{" "}
            <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
              Get a free SEO audit →
            </Link>
          </p>
        )}
        {answeredCount > 0 && !allDone && fixNext.length === 0 && checkFirst.length === 0 && (
          <p className="mt-1 text-sm">No gaps so far. Keep going.</p>
        )}
      </div>
    </section>
  );
}
